import { Controller } from "@hotwired/stimulus";

const NARROW_STORAGE_KEY = "coreui-sidebar-narrow";
const WIDTH_STORAGE_KEY = "coreui-sidebar-width";
const DEFAULT_WIDTH = 256;
const MIN_WIDTH = 236;
const MAX_WIDTH = 560;
const MIN_CONTENT_WIDTH = 320;
const DESKTOP_MEDIA_QUERY = "(min-width: 992px)";

export default class extends Controller {
  initialize() {
    this.resize = this.resize.bind(this);
    this.stopResize = this.stopResize.bind(this);
    this.syncWidthToViewport = this.syncWidthToViewport.bind(this);
  }

  connect() {
    this.mount();
  }

  mount() {
    if (!this.mounted) {
      this.addResizeHandle();
      window.addEventListener("resize", this.syncWidthToViewport);
      this.mounted = true;
    }
    // Browser history can reuse a live controller with an older DOM snapshot.
    this.element.classList.remove("sidebar-narrow");
    this.element.classList.toggle("sidebar-narrow-unfoldable", localStorage.getItem(NARROW_STORAGE_KEY) === "true");
    this.syncToggleState();
    this.syncWidthToViewport();
  }

  disconnect() {
    this.mounted = false;
    window.removeEventListener("resize", this.syncWidthToViewport);
    this.removeResizeListeners();
    document.body.classList.remove("sidebar-is-resizing");
    this.resizeHandle?.remove();
    this.element.classList.remove("sidebar-resizable");
  }

  storeNarrowState() {
    // The component emits after toggling. Persist synchronously before a visit
    // or Back operation can freeze the page's animation frames.
    const isNarrow = this.element.classList.contains("sidebar-narrow-unfoldable");
    localStorage.setItem(NARROW_STORAGE_KEY, String(isNarrow));
    this.syncToggleState();
  }

  syncToggleState() {
    this.element.querySelector('[data-coreui-toggle="unfoldable"]')?.setAttribute(
      "aria-expanded", String(!this.element.classList.contains("sidebar-narrow-unfoldable"))
    );
  }

  startResize(event) {
    if (event.button !== 0 || !this.isDesktop() || this.element.classList.contains("sidebar-narrow-unfoldable")) return;

    event.preventDefault();
    this.activePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.element.getBoundingClientRect().width;
    this.resizeDirection = getComputedStyle(this.element).direction === "rtl" ? -1 : 1;
    this.resizeHandle.setPointerCapture(event.pointerId);
    document.body.classList.add("sidebar-is-resizing");
    window.addEventListener("pointermove", this.resize);
    window.addEventListener("pointerup", this.stopResize);
    window.addEventListener("pointercancel", this.stopResize);
  }

  resize(event) {
    if (event.pointerId !== this.activePointerId) return;

    const distance = (event.clientX - this.resizeStartX) * this.resizeDirection;
    this.applyWidth(this.resizeStartWidth + distance);
  }

  stopResize(event) {
    if (event.pointerId !== this.activePointerId) return;

    const width = this.element.getBoundingClientRect().width;
    localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
    this.removeResizeListeners();
    document.body.classList.remove("sidebar-is-resizing");
  }

  resizeWithKeyboard(event) {
    const direction = getComputedStyle(this.element).direction === "rtl" ? -1 : 1;
    const step = event.shiftKey ? 32 : 16;
    let adjustment;

    if (event.key === "ArrowLeft") adjustment = -step * direction;
    if (event.key === "ArrowRight") adjustment = step * direction;
    if (event.key === "Home") adjustment = MIN_WIDTH - this.element.getBoundingClientRect().width;
    if (adjustment === undefined) return;

    event.preventDefault();
    const width = this.element.getBoundingClientRect().width + adjustment;
    const appliedWidth = this.applyWidth(width);
    localStorage.setItem(WIDTH_STORAGE_KEY, String(appliedWidth));
  }

  addResizeHandle() {
    // Turbo snapshots can contain the old handle before Stimulus disconnects.
    this.element.querySelectorAll(".sidebar-resize-handle").forEach((handle) => handle.remove());
    this.element.classList.add("sidebar-resizable");
    this.resizeHandle = document.createElement("div");
    this.resizeHandle.className = "sidebar-resize-handle";
    this.resizeHandle.tabIndex = 0;
    this.resizeHandle.setAttribute("role", "separator");
    this.resizeHandle.setAttribute("aria-label", "调整侧边栏宽度");
    this.resizeHandle.setAttribute("aria-orientation", "vertical");
    this.resizeHandle.setAttribute("aria-valuemin", String(MIN_WIDTH));
    this.resizeHandle.setAttribute("title", "拖动调整侧边栏宽度");
    this.resizeHandle.dataset.action = [
      "pointerdown->portal-sidebar#startResize",
      "keydown->portal-sidebar#resizeWithKeyboard"
    ].join(" ");
    this.element.appendChild(this.resizeHandle);
  }

  syncWidthToViewport() {
    if (!this.isDesktop()) {
      this.removeResizeListeners();
      document.body.classList.remove("sidebar-is-resizing");
      this.element.style.removeProperty("--cui-sidebar-width");
      this.element.parentElement.style.removeProperty("--app-sidebar-width");
      return;
    }

    const storedWidth = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
    this.applyWidth(Number.isFinite(storedWidth) && storedWidth > 0 ? storedWidth : DEFAULT_WIDTH);
  }

  applyWidth(width) {
    const maximumWidth = this.maximumWidth();
    const appliedWidth = Math.round(Math.min(Math.max(width, MIN_WIDTH), maximumWidth));
    this.element.style.setProperty("--cui-sidebar-width", `${appliedWidth}px`);
    this.element.parentElement.style.setProperty("--app-sidebar-width", `${appliedWidth}px`);
    this.resizeHandle.setAttribute("aria-valuemax", String(maximumWidth));
    this.resizeHandle.setAttribute("aria-valuenow", String(appliedWidth));
    return appliedWidth;
  }

  maximumWidth() {
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - MIN_CONTENT_WIDTH));
  }

  isDesktop() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }

  removeResizeListeners() {
    window.removeEventListener("pointermove", this.resize);
    window.removeEventListener("pointerup", this.stopResize);
    window.removeEventListener("pointercancel", this.stopResize);
    this.activePointerId = undefined;
  }
}
